import type { Dayjs } from 'dayjs';
import type { MissionProfileType, MissionMetaType, ObstaclePolicy, ObstaclePolicyMode, ScanMode, VehicleClass } from './missionCommandCatalogue';

export type { MissionProfileType, MissionMetaType, ObstaclePolicy, ObstaclePolicyMode, ScanMode, VehicleClass };

export type AreaItem = {
    id: number;
    name: string;
    code?: string | null;
    companyId?: number;
    geom?: any;
    configurations?: Record<string, any> | null;
    spaceType?: string | null;
    slamMapId?: number | null;
    slamMap?: SlamMapItem | null;
    parentAreaId?: number | null;
    parentArea?: AreaItem | null;
    children?: AreaItem[];
    hierarchyDepth?: number;
    areaType?: {
        id: number;
        name: string;
        maxVertices?: number | null;
        templateImage?: string | null;
        patternCoordinateMode?: 'gps' | 'normalized' | null;
        templateDimensions?: {
            widthMeters: number;
            heightMeters: number;
        } | null;
    } | null;
};

export type DeviceModuleItem = {
    id?: number;
    moduleDefinitionId?: number | null;
    moduleDefinition?: {
        id: number;
        key: string;
        name: string;
        defaultTriggerMethod?: string | null;
        configSchema?: Record<string, any> | null;
        defaultConfig?: Record<string, any> | null;
        commandSpecs?: any[] | null;
        outputSchema?: Record<string, any> | null;
    } | null;
    name?: string | null;
    instanceKey?: string | null;
    status?: string | null;
    isDefault?: boolean;
    sortOrder?: number;
    config?: Record<string, any> | null;
    calibration?: Record<string, any> | null;
    metadata?: Record<string, any> | null;
};

export type DeviceCapabilityItem = {
    id?: number;
    key?: string;
    name?: string;
    category?: string;
    enabled?: boolean;
    capabilityDefinitionId?: number | null;
    capabilityDefinition?: {
        id: number;
        key: string;
        name: string;
        category: string;
    } | null;
    definition?: {
        id: number;
        key: string;
        name: string;
        category: string;
    } | null;
};

export type VehicleProfileItem = {
    id: number;
    key: string;
    name: string;
    vehicleClass: string;
    movementType: string;
    description?: string | null;
    supportsAltitude: boolean;
    supportsTakeoffLand: boolean;
    supportsDocking: boolean;
    supportsReverse: boolean;
    obstacleAvoidance: boolean;
    maxSpeed?: number | null;
    defaultCruiseSpeed?: number | null;
    defaultTurnRadius?: number | null;
    defaultAltitude?: number | null;
    defaultAltitudeMode?: string | null;
    availableCommands?: string[] | null;
    supportedModes?: string[] | null;
    firmwareType?: number | null;
    defaultFrame?: number | null;
};

export type DeviceItem = {
    id: number;
    label: string;
    currentStatus?: string | null;
    lastHeartbeat?: string | null;
    lastLocation?: any;
    statusSnapshot?: Record<string, any> | null;
    statusSnapshotAt?: string | null;
    deviceType?: {
        id: number;
        name: string;
        commandProtocol?: string | null;
        vehicleCapabilities?: MissionProfileType | null;
        vehicleProfile?: VehicleProfileItem | null;
        vehicleProfileId?: number | null;
        moduleTemplates?: DeviceModuleItem[] | null;
        capabilities?: DeviceCapabilityItem[] | null;
    } | null;
    modules?: DeviceModuleItem[] | null;
    effectiveCapabilities?: DeviceCapabilityItem[] | null;
};

export const isVehicleDevice = (device: Pick<DeviceItem, 'deviceType'> | null | undefined): boolean => (
    device?.deviceType?.vehicleProfile != null ||
    device?.deviceType?.vehicleCapabilities != null
);

export type PatternItem = {
    id: number;
    name: string;
    areaId?: number | null;
    area_id?: number | null;
    areaTypeId?: number | null;
    area_type_id?: number | null;
    patternType?: string | null;
    coordSystem?: string | null;
    isGlobal?: boolean;
    numPoints?: number | null;
    definition?: {
        boxWidth?: number | null;
        boxHeight?: number | null;
        [key: string]: any;
    } | null;
};

export type PatternPointItem = {
    id: number;
    pointIndex: number;
    label?: string | null;
    gpsLat?: number | null;
    gpsLon?: number | null;
    gpsAlt?: number | null;
    xLocal?: number | null;
    yLocal?: number | null;
};

export type LocalPose = {
    coordinateSystem?: string | null;
    frameId?: string | null;
    x: number;
    y: number;
    z?: number | null;
    yaw?: number | null;
    source?: string | null;
    slamMapId?: number | null;
    datumId?: string | number | null;
    projectionMode?: string | null;
    [key: string]: any;
};

export type MissionPointItem = {
    id: number;
    seq: number;
    pointIndex: number;
    label?: string | null;
    actionType: string;
    status: string;
    patternPointId?: number | null;
    waitSeconds?: number | null;
    speedOverride?: number | null;
    localPose?: LocalPose | null;
    isRequired: boolean;
    params?: Record<string, any>;
    source?: 'pattern' | 'custom';
    resolvedLocation?: {
        lat: number;
        lng: number;
    } | null;
    patternPoint?: PatternPointItem | null;
};

export type CommandProtocol = 'oscar' | 'mavlink' | 'ardupilot' | 'custom';

export type OperationEnvironment = 'outdoor' | 'indoor' | 'mixed' | 'scan';
export type MissionNavigationMode = 'route_following' | 'waypoint_goals';

export type MissionLegConstraints = {
    motionConstraint?: {
        mode: 'normal' | 'steering_locked';
        steeringAngleDegrees?: number;
        sectionId?: string | null;
        entryHeadingToleranceDegrees?: number;
        entryApproachDistanceMeters?: number;
        maxCrossTrackMeters?: number;
        obstacleBehavior?: 'stop';
    } | null;
};

export const OPERATION_ENVIRONMENT_OPTIONS: Array<{
    value: OperationEnvironment;
    label: string;
    description: string;
    examples: string;
}> = [
    {
        value: 'outdoor',
        label: 'Outdoor (GPS)',
        description: 'Full GPS signal available. Satellite map and compass headings used for navigation.',
        examples: 'Open sports pitch, farm field, golf course, vineyard',
    },
    {
        value: 'indoor',
        label: 'Indoor (local nav)',
        description: 'No reliable GPS. Robot navigates using a SLAM map, local coordinates, markers, or LiDAR.',
        examples: 'Warehouse, indoor arena, greenhouse, exhibition hall',
    },
    {
        value: 'mixed',
        label: 'Mixed (indoor + outdoor)',
        description: 'Partial GPS coverage. Indoor segments use geo-anchored SLAM maps overlaid on the satellite view.',
        examples: 'Stadium with retractable roof, covered tennis centre, partially roofed facility',
    },
    {
        value: 'scan',
        label: 'Scan mission',
        description: 'Autonomous scan without a pre-selected area. The robot explores and maps the environment freely.',
        examples: 'Initial site survey, SLAM map creation, unmapped facility exploration',
    },
];

export type MissionCommandKind = 'nav' | 'do' | 'condition' | 'custom';

export type MissionCommandItem = {
    id: number | string;
    seq: number;
    label?: string | null;
    kind: MissionCommandKind | string;
    commandName: string;
    commandCode?: number | null;
    frame?: number | null;
    autoContinue: boolean;
    missionPointId?: number | null;
    missionPointIndex?: number | null;
    altitude?: number | null;
    localPose?: LocalPose | null;
    params?: Record<string, any>;
    source?: string | null;
    isPrimary: boolean;
    /** Commands with the same executionGroup (per point) run in parallel; groups run sequentially. */
    executionGroup?: number | null;
    resolvedLocation?: {
        lat: number;
        lng: number;
    } | null;
};

export type MissionLegItem = {
    id: number;
    seq: number;
    fromMissionPointId: number;
    toMissionPointId: number;
    fromPointIndex?: number | null;
    toPointIndex?: number | null;
    pathGeom?: any;
    distanceMeters?: number | null;
    estimatedDurationSeconds?: number | null;
    status: string;
    routingMode: string;
    constraints?: MissionLegConstraints | null;
};

export type MissionRouteMode = 'direct' | 'curve' | 'maneuver' | 'dubins';

export type DubinsCostMode = 'shortest' | 'smoothest' | 'balanced';

export const DUBINS_COST_MODE_OPTIONS: Array<{ value: DubinsCostMode; label: string }> = [
    { value: 'shortest', label: 'Shortest path' },
    { value: 'balanced', label: 'Balanced' },
    { value: 'smoothest', label: 'Smoothest path' },
];

export type MissionRouteConfig = {
    turnRadiusMeters: number;
    sampleStepMeters: number;
    routingMode: MissionRouteMode;
    patternGuidedDirections?: boolean;
    costMode?: DubinsCostMode;
    optimisationEffort?: number;
    /** Number of evenly-spaced heading angles to try per point (10–100, default 24) */
    sweepAngles?: number;
    /** How many points to evaluate in each greedy window (2–N, default 2) */
    windowSize?: number;
};

/** Frontend type for a SLAM map record returned by the API */
export type SlamMapItem = {
    id: number;
    name: string;
    description?: string | null;
    mapType: 'slam_2d' | 'floor_plan' | 'cad_overlay' | '3d_reconstruction' | string;
    imageStoragePath?: string | null;
    imageWidth?: number | null;
    imageHeight?: number | null;
    metersPerPixel?: number | null;
    originX?: number | null;
    originY?: number | null;
    rotationDegrees?: number | null;
    geoAnchor?: {
        lat: number;
        lng: number;
        anchorPixelX: number;
        anchorPixelY: number;
        bearingDegrees: number;
    } | null;
    areaId: number;
    isDeleted?: boolean;
    createdAt?: string;
    updatedAt?: string;
};

export type GeneralMarkerItem = {
    id: number;
    areaId?: number | null;
    missionId?: number | null;
    slamMapId?: number | null;
    categoryId?: number | null;
    category?: {
        id: number;
        key: string;
        name: string;
        style?: Record<string, any> | null;
    } | null;
    markerType?: string | null;
    source?: string | null;
    label?: string | null;
    status?: string | null;
    geom?: any | null;
    localPose?: LocalPose | null;
    gpsLat?: number | null;
    gpsLng?: number | null;
    slamX?: number | null;
    slamY?: number | null;
    capturedAt?: string | null;
    metadata?: Record<string, any> | null;
};

/** A SLAM map attached to a mission (join record) */
export type MissionSlamMapItem = {
    id: number;
    missionId: number;
    slamMapId: number;
    isActive: boolean;
    sortOrder: number;
    slamMap?: SlamMapItem;
    createdAt?: string;
};

export type MissionItem = {
    id: number;
    name: string;
    description?: string | null;
    missionType?: string | null;
    operationEnvironment?: OperationEnvironment | string | null;
    navigationMode?: MissionNavigationMode | null;
    status: string;
    companyId: number;
    areaId?: number | null;
    deviceId: number;
    patternId?: number | null;
    externalSource?: string | null;
    externalReference?: string | null;
    externalApiTokenId?: number | null;
    createdAt?: string;
    updatedAt?: string;
    scheduledStartAt?: string | null;
    startedAt?: string | null;
    completedAt?: string | null;
    cancelledAt?: string | null;
    routeConfig?: MissionRouteConfig | null;
    missionMeta?: MissionMetaType | null;
    area?: AreaItem | null;
    device?: DeviceItem | null;
    pattern?: PatternItem | null;
    missionPoints?: MissionPointItem[];
    missionCommands?: MissionCommandItem[];
    missionLegs?: MissionLegItem[];
    measurementCount?: number;
    lastMeasurementAt?: string | null;
    pointCount?: number;
    legCount?: number;
    routeSummary?: {
        pointCount: number;
        legCount: number;
        totalDistanceMeters: number;
        totalDistanceKm: number;
        estimatedDurationSeconds: number;
        estimatedDurationMinutes: number;
    };
};

export type MissionPointDraft = {
    key: string;
    id?: number;
    label: string;
    actionType: string;
    patternPointId?: number | null;
    lat?: number | null;
    lng?: number | null;
    localPose?: LocalPose | null;
    waitSeconds?: number | null;
    speedOverride?: number | null;
    isRequired: boolean;
    source: 'pattern' | 'custom';
    params: Record<string, any>;
};

export type MissionCommandDraft = {
    key: string;
    missionPointKey?: string | null;
    label: string;
    kind: MissionCommandKind | string;
    commandName: string;
    commandCode?: number | null;
    frame?: number | null;
    autoContinue: boolean;
    altitude?: number | null;
    headingDegrees?: number | null;
    lat?: number | null;
    lng?: number | null;
    localPose?: LocalPose | null;
    params: Record<string, any>;
    source: string;
    isPrimary: boolean;
    /** Commands with the same executionGroup (per point) run in parallel; groups execute sequentially. null = each command gets its own implicit sequential group. */
    executionGroup?: number | null;
};

export type MissionFormValues = {
    name: string;
    description?: string;
    missionType?: string;
    operationEnvironment?: OperationEnvironment;
    navigationMode?: MissionNavigationMode;
    areaId?: number | null;
    deviceId: number;
    patternId?: number | null;
    turnRadiusMeters?: number | null;
    defaultSpeedMps?: number | null;
    scheduledStartAt?: Dayjs | null;
    status: string;
};

export type MissionSavePayload = {
    name: string;
    description?: string | null;
    missionType: string;
    operationEnvironment?: OperationEnvironment;
    navigationMode?: MissionNavigationMode;
    missionMeta?: MissionMetaType | Record<string, any> | null;
    /** IDs of SLAM maps to attach to this mission (for indoor/mixed environments) */
    slamMapIds?: number[];
    areaId?: number | null;
    deviceId: number;
    patternId?: number | null;
    routeConfig?: Partial<MissionRouteConfig> | null;
    defaultSpeedMps?: number | null;
    scheduledStartAt?: string | null;
    status: string;
    missionPoints: Array<{
        label?: string | null;
        actionType: string;
        patternPointId?: number | null;
        geom?: {
            type: 'Point';
            coordinates: [number, number];
        } | null;
        localPose?: LocalPose | null;
        waitSeconds?: number | null;
        speedOverride?: number | null;
        params?: Record<string, any>;
        isRequired: boolean;
    }>;
    missionCommands?: Array<{
        label?: string | null;
        kind: MissionCommandKind | string;
        commandName: string;
        commandCode?: number | null;
        frame?: number | null;
        autoContinue: boolean;
        missionPointIndex?: number | null;
        geom?: {
            type: 'Point';
            coordinates: [number, number];
        } | null;
        localPose?: LocalPose | null;
        altitude?: number | null;
        headingDegrees?: number | null;
        params?: Record<string, any>;
        source?: string | null;
        isPrimary: boolean;
        executionGroup?: number | null;
    }>;
    missionLegs?: Array<{
        fromPointIndex: number;
        toPointIndex: number;
        seq: number;
        routingMode: string;
        pathCoordinates: Array<{ lat: number; lng: number }>;
        distanceMeters?: number | null;
        constraints?: MissionLegConstraints | null;
    }>;
};

export type MissionImportPreview = {
    format: 'qgc_plan' | 'qgc_wpl';
    missionName: string | null;
    summary: {
        itemCount: number;
        pointCount: number;
        commandCount: number;
        unsupportedCount: number;
        warningCount: number;
    };
    metadata: Record<string, any>;
    warnings: string[];
    unsupportedItems: Array<{
        seq: number;
        commandName: string;
        reason: string;
    }>;
    missionPoints: MissionSavePayload['missionPoints'];
    missionCommands: NonNullable<MissionSavePayload['missionCommands']>;
};

export type MapPath = {
    id: number | string;
    coordinates: Array<{ lat: number; lng: number }>;
    selected?: boolean;
    warning?: boolean;
    previewOnly?: boolean;
};

export type MissionEditorMode = 'create' | 'edit';

export const ACTION_OPTIONS = [
    { value: 'start', label: 'Start' },
    { value: 'move', label: 'Move' },
    { value: 'measure', label: 'Measure' },
    { value: 'wait', label: 'Wait' },
    { value: 'dock', label: 'Dock' },
    { value: 'end', label: 'End' },
    { value: 'custom', label: 'Custom' },
];

export const MISSION_TYPE_OPTIONS = [
    { value: 'robot_run', label: 'Robot run' },
    { value: 'field_scan', label: 'Field scan' },
    { value: 'sampling', label: 'Sampling' },
    { value: 'inspection', label: 'Inspection' },
    { value: 'slam_scan', label: 'Map scanning (SLAM creation)' },
];

export const MISSION_STATUS_OPTIONS = [
    { value: 'draft', label: 'Draft' },
    { value: 'scheduled', label: 'Scheduled' },
    { value: 'running', label: 'Running' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' },
];

export const MISSION_COMMAND_KIND_OPTIONS = [
    { value: 'nav', label: 'Navigation' },
    { value: 'do', label: 'DO command' },
    { value: 'condition', label: 'Condition' },
    { value: 'custom', label: 'Custom' },
];

export const MISSION_COMMAND_NAME_OPTIONS: Record<string, Array<{ value: string; label: string }>> = {
    nav: [
        { value: 'OSCAR_START', label: 'OSCAR Start' },
        { value: 'OSCAR_MOVE', label: 'OSCAR Move' },
        { value: 'OSCAR_MEASURE', label: 'OSCAR Measure' },
        { value: 'OSCAR_DOCK', label: 'OSCAR Dock' },
        { value: 'OSCAR_END', label: 'OSCAR End' },
        { value: 'OSCAR_PASS_THROUGH', label: 'OSCAR Pass Through' },
        { value: 'MAV_CMD_NAV_WAYPOINT', label: 'MAV_CMD_NAV_WAYPOINT' },
        { value: 'MAV_CMD_NAV_TAKEOFF', label: 'MAV_CMD_NAV_TAKEOFF' },
        { value: 'MAV_CMD_NAV_LAND', label: 'MAV_CMD_NAV_LAND' },
        { value: 'MAV_CMD_NAV_RETURN_TO_LAUNCH', label: 'MAV_CMD_NAV_RETURN_TO_LAUNCH' },
    ],
    do: [
        { value: 'MAV_CMD_DO_CHANGE_SPEED', label: 'MAV_CMD_DO_CHANGE_SPEED' },
        { value: 'MAV_CMD_DO_SET_HOME', label: 'MAV_CMD_DO_SET_HOME' },
        { value: 'MAV_CMD_DO_SET_SERVO', label: 'MAV_CMD_DO_SET_SERVO' },
        { value: 'MAV_CMD_DO_SET_RELAY', label: 'MAV_CMD_DO_SET_RELAY' },
        { value: 'MAV_CMD_DO_JUMP', label: 'MAV_CMD_DO_JUMP' },
    ],
    condition: [
        { value: 'OSCAR_WAIT', label: 'OSCAR Wait' },
        { value: 'MAV_CMD_CONDITION_DELAY', label: 'MAV_CMD_CONDITION_DELAY' },
        { value: 'MAV_CMD_CONDITION_DISTANCE', label: 'MAV_CMD_CONDITION_DISTANCE' },
    ],
    custom: [
        { value: 'OSCAR_CUSTOM', label: 'OSCAR Custom' },
        { value: 'MAV_CMD_CUSTOM', label: 'MAV_CMD Custom' },
    ],
};

export const statusColorMap: Record<string, string> = {
    draft: 'default',
    scheduled: 'blue',
    running: 'green',
    completed: 'purple',
    cancelled: 'red',
    archived: 'default',
    online: 'green',
    offline: 'default',
    maintenance: 'gold',
    error: 'red',
    scan: 'purple',
};

export const getCompactDeviceStatus = (value: unknown): string | null => {
    if (typeof value !== 'string') {
        return null;
    }

    const status = value.trim();
    if (!status || status.length > 32 || !/^[a-zA-Z][a-zA-Z0-9 _-]*$/.test(status)) {
        return null;
    }

    return status;
};

export const formatStatusLabel = (status: string) => status.replace(/[_-]+/g, ' ');
